import type { CartElement } from "../../global";
import type { SubscriptionFormContextType } from "./SubscriptionFormContext";

interface VariantSelectionParams {
  isSubscribing: boolean;
  isSubscribingToRefills: boolean;
  sellingPlanId: string;
  productIds: SubscriptionFormContextType["productIds"];
}

export function resolveVariantSelection({
  isSubscribing,
  isSubscribingToRefills,
  sellingPlanId,
  productIds,
}: VariantSelectionParams): { id: string; sellingPlan?: string } {
  if (!isSubscribing) return { id: productIds.baseProductId };
  return {
    id: isSubscribingToRefills
      ? productIds.refillProductId
      : productIds.fullApplicatorProductId,
    sellingPlan: sellingPlanId,
  };
}

interface AddToCartParams {
  id: string;
  sellingPlan?: string;
  quantity: number;
}

// Mirrors the AJAX add-to-cart + drawer-open flow in apps/theme/assets/product-form.js,
// triggered from a user click instead of a <product-form> custom element's submit
// interception (which can't reliably attach before this island hydrates).
export async function addToCart({ id, sellingPlan, quantity }: AddToCartParams): Promise<void> {
  const cart = (document.querySelector("cart-notification") ||
    document.querySelector("cart-drawer")) as CartElement | null;

  const formData = new FormData();
  formData.append("id", id);
  if (sellingPlan) formData.append("selling_plan", sellingPlan);
  formData.append("quantity", String(quantity));

  if (cart) {
    formData.append(
      "sections",
      cart.getSectionsToRender().map((section) => section.id).join(",")
    );
    formData.append("sections_url", window.location.pathname);
  }

  const response = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
    method: "POST",
    headers: { Accept: "application/javascript", "X-Requested-With": "XMLHttpRequest" },
    body: formData,
  });
  const data = await response.json();

  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || "Failed to add to cart");
  }

  if (!cart) {
    window.location.href = window.routes?.cart_url || "/cart";
    return;
  }

  cart.renderContents(data);

  const globalWindow = window as unknown as {
    publish?: (eventName: string, payload: unknown) => void;
  };
  globalWindow.publish?.("cart-update", {
    source: "subscription-form",
    productVariantId: id,
    cartData: data,
  });
}
